import { EVENT_NAMES } from '@/constants';
import { onMounted, onUnmounted } from 'vue';
import { Rectangle } from 'pixi.js';
import { jsPDF } from 'jspdf';
import { emitter } from '@/utils';
import type { EditorCanvas } from '@/modules/editor/canvas/EditorCanvas.class';
import { useExportStore } from '@/store';

export const useExport = (targetCanvas: EditorCanvas) => {
  const canvas = targetCanvas;
  const exportStore = useExportStore();

  /**
   * 根据 pageMap 中的所有页生成 PDF 文件
   * 每一页 BookPage 截图后作为 PDF 的一页，页尺寸与画布页一致
   * @param filename 输出文件名
   * @param resolution 截图分辨率倍数，越大越清晰（默认 1）
   */
  const exportPDF = async (filename = 'picture-book.pdf', resolution = 1) => {
    const app = canvas.getApp();
    if (!app) return;

    const pages = canvas.getPages();
    if (pages.length === 0) return;

    // 暂停主渲染循环：导出时需逐页临时显示再截图，
    // 停掉 ticker 可避免这些中间状态被绘制到屏幕造成闪烁
    const ticker = app.ticker;
    const wasRunning = ticker.started;
    ticker.stop();

    // 标准文档宽度 (mm)，对应 A4 宽度，100% 查看时显示为真实物理尺寸
    const PDF_WIDTH_MM = 210;

    // 根据第一页宽高比创建 PDF，后续页通过 addPage 追加
    const first = pages[0];
    const firstPageH = PDF_WIDTH_MM * (first.pageHeight / first.pageWidth);
    const pdf = new jsPDF({
      orientation: PDF_WIDTH_MM >= firstPageH ? 'landscape' : 'portrait',
      unit: 'mm',
      format: [PDF_WIDTH_MM, firstPageH],
      compress: true
    });

    try {
      for (let i = 0; i < pages.length; i++) {
        // 更新进度文案，并让出一帧让 UI 重绘（下方截图/编码是同步阻塞的）
        exportStore.updateProgress(
          Number((((i + 1) / pages.length) * 100).toFixed(2)),
          `导出页数 ${i + 1}/${pages.length}`
        );
        await new Promise((resolve) => setTimeout(resolve, 0));

        const page = pages[i];
        const w = page.pageWidth;
        const h = page.pageHeight;
        if (w === 0 || h === 0) continue;

        // 按页面宽高比计算本页 PDF 物理高度（不同页比例可能不同）
        const pdfHeightMM = PDF_WIDTH_MM * (h / w);

        // 第一页已在构造函数中创建，后续页通过 addPage 追加
        if (i > 0) {
          pdf.addPage(
            [PDF_WIDTH_MM, pdfHeightMM],
            PDF_WIDTH_MM >= pdfHeightMM ? 'landscape' : 'portrait'
          );
        }

        // 页内容相对自身原点居中绘制（-w/2 ~ w/2），需用显式 frame 才能完整截取整页
        const wasVisible = page.visible;
        page.visible = true;
        const wasMaskEnabled = page.maskEnabled;
        page.setMaskEnabled(true);
        try {
          // 高分辨率截图：像素多 → 放入物理尺寸(mm)的页面中 → 100% 查看时清晰且大小合理
          page.hideEventElements();
          page.showWatermark();
          const canvasEl = app.renderer.extract.canvas({
            target: page,
            frame: new Rectangle(-w / 2, -h / 2, w, h),
            resolution
          }) as HTMLCanvasElement;

          // 高像素图片放入较小的物理尺寸中 → 高 DPI，100% 查看时清晰不模糊
          pdf.addImage(canvasEl, 'PNG', 0, 0, PDF_WIDTH_MM, pdfHeightMM);
        } finally {
          page.hideWatermark();
          page.showEventElements();
          page.setMaskEnabled(wasMaskEnabled);
          page.visible = wasVisible;
        }
      }

      // 生成 Blob 并通知 store 进入完成态，由用户点击下载
      const blob = pdf.output('blob');
      exportStore.showComplete(blob, filename);
    } finally {
      // 步骤1：兜底恢复所有页的交互按钮（防止中途异常导致仍隐藏）
      pages.forEach((page) => page.showEventElements());
      // 步骤2：恢复主循环并立刻重绘，确保关闭导出后画布上按钮可见
      if (wasRunning) ticker.start();
      app.render();
    }
  };

  /**
   * 导出所有页为 JPG 图片，打包成 zip 压缩包
   * @param filename 输出文件名（不含扩展名）
   * @param resolution 截图分辨率倍数，越大越清晰（默认 2）
   * @param quality JPEG 质量 0-1（默认 0.92）
   */
  const exportJPG = async (filename = 'picture-book', resolution = 1, quality = 0.92) => {
    const app = canvas.getApp();
    if (!app) return;

    const pages = canvas.getPages();
    if (pages.length === 0) return;

    const ticker = app.ticker;
    const wasRunning = ticker.started;
    ticker.stop();

    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      for (let i = 0; i < pages.length; i++) {
        exportStore.updateProgress(
          Number((((i + 1) / pages.length) * 100).toFixed(2)),
          `导出页数 ${i + 1}/${pages.length}`
        );
        await new Promise((resolve) => setTimeout(resolve, 0));

        const page = pages[i];
        const w = page.pageWidth;
        const h = page.pageHeight;
        if (w === 0 || h === 0) continue;

        const wasVisible = page.visible;
        page.visible = true;
        const wasMaskEnabled = page.maskEnabled;
        page.setMaskEnabled(true);
        try {
          page.hideEventElements();
          page.showWatermark();
          const extractedCanvas = app.renderer.extract.canvas({
            target: page,
            frame: new Rectangle(-w / 2, -h / 2, w, h),
            resolution
          }) as HTMLCanvasElement;

          const dataUrl = extractedCanvas.toDataURL('image/jpeg', quality);
          const base64Data = dataUrl.split(',')[1];
          zip.file(`${filename}-${String(i + 1).padStart(2, '0')}.jpg`, base64Data, {
            base64: true
          });
        } finally {
          page.hideWatermark();
          page.showEventElements();
          page.setMaskEnabled(wasMaskEnabled);
          page.visible = wasVisible;
        }
      }

      // 生成 Blob 并通知 store 进入完成态，由用户点击下载
      const blob = await zip.generateAsync({ type: 'blob' });
      exportStore.showComplete(blob, `${filename}.zip`);
    } finally {
      // 步骤1：兜底恢复所有页的交互按钮（防止中途异常导致仍隐藏）
      pages.forEach((page) => page.showEventElements());
      // 步骤2：恢复主循环并立刻重绘，确保关闭导出后画布上按钮可见
      if (wasRunning) ticker.start();
      app.render();
    }
  };

  // ===== 通过 mitt 事件监听导出请求 =====

  const handleExportPDF = async (params: { filename?: string }) => {
    exportStore.showExporting('pdf');
    await new Promise((resolve) => setTimeout(resolve, 50));
    try {
      await exportPDF(params.filename);
    } catch (err: unknown) {
      console.error('导出PDF失败:', err);
      exportStore.close();
    }
  };

  const handleExportJPG = async (params: { filename?: string; quality?: number }) => {
    exportStore.showExporting('jpg');
    await new Promise((resolve) => setTimeout(resolve, 50));
    try {
      await exportJPG(params.filename, 1, params.quality);
    } catch (err: unknown) {
      console.error('导出JPG失败:', err);
      exportStore.close();
    }
  };

  onMounted(() => {
    emitter.on(EVENT_NAMES.EXPORT_PDF, handleExportPDF);
    emitter.on(EVENT_NAMES.EXPORT_JPG, handleExportJPG);
  });

  onUnmounted(() => {
    emitter.off(EVENT_NAMES.EXPORT_PDF, handleExportPDF);
    emitter.off(EVENT_NAMES.EXPORT_JPG, handleExportJPG);
  });

  return { exportPDF, exportJPG };
};
